import { useRouteError, isRouteErrorResponse, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trackException } from "@/telemetry/ai";

/**
 * Error boundary component for React Router 7.
 * Handles both runtime errors and loader/action errors.
 */
export default function ErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof Error) {
      trackException(error, {
        component: "ErrorBoundary",
        type: "RouteError",
      });
    } else if (isRouteErrorResponse(error)) {
      trackException(new Error(`${error.status} ${error.statusText}`), {
        component: "ErrorBoundary",
        type: "RouteErrorResponse",
        status: error.status,
        data: JSON.stringify(error.data),
      });
    } else {
      trackException(new Error(String(error)), {
        component: "ErrorBoundary",
        type: "UnknownRouteError",
      });
    }
  }, [error]);

  let title = "Something went wrong";
  let message = "An unexpected error occurred.";
  let statusCode: number | undefined;

  if (isRouteErrorResponse(error)) {
    statusCode = error.status;
    title = `${error.status} ${error.statusText}`;
    message = error.data?.message || error.statusText;
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="min-h-screen bg-surface-background-light dark:bg-surface-background-dark flex items-center justify-center px-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-destructive">{statusCode ? `Error ${statusCode}` : "Error"}</CardTitle>
          <CardDescription className="text-lg font-semibold">{title}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground-muted-light dark:text-foreground-muted-dark">{message}</p>

          {import.meta.env.DEV && error instanceof Error && error.stack && (
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold">Stack trace</summary>
              <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-[10px]">{error.stack}</pre>
            </details>
          )}

          <div className="flex gap-2">
            <Button asChild>
              <Link to="/" prefetch="intent">
                Go Home
              </Link>
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
